<template>
  <v-row>
    <v-col cols="12">
      <v-form :submit="submitUpdate">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="Nome"
              v-model="value.name"
              :rules="rules.required"
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              label="Tipo"
              v-model="value.type"
              :items="storeTypes"
              item-title="title"
              item-value="value"
              :rules="rules.required"
              density="compact"
              :disabled="requestingApi"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              label="Status"
              v-model="value.status"
              :items="storeStatuses"
              item-title="title"
              item-value="value"
              :rules="rules.required"
              density="compact"
              :disabled="requestingApi"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Início"
              v-model="value.starts_at"
              type="date"
              clearable
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Fim"
              v-model="value.ends_at"
              type="date"
              clearable
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="value.responsible_user_id"
              v-model:search="managerSearch"
              :items="managerItems"
              item-title="title"
              item-value="value"
              label="Gerente (responsável)"
              :rules="rules.required"
              :loading="loadingManagers"
              :disabled="requestingApi"
              density="compact"
              clearable
              hide-details="auto"
              no-filter
              :no-data-text="managerNoDataText"
            >
              <template v-slot:no-data>
                <div class="px-4 py-2 text-medium-emphasis">{{ managerNoDataText }}</div>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="6">
            <AutocompleteSelectCashflowCategory
              label="Rubrica (opcional)"
              :user="user"
              v-model="value.cashflow_category_id"
              :groupId="selectedGroupId"
              :returnObject="false"
              :multiple="false"
              :clearable="true"
              :disabled="requestingApi"
            />
          </v-col>
          <v-col cols="12">
            <div class="text-subtitle-2 mb-1">Pagamentos no PDV / web</div>
            <div class="d-flex flex-wrap ga-6">
              <v-switch
                v-model="value.pix_enabled"
                label="Pix"
                color="primary"
                density="compact"
                hide-details
                :disabled="requestingApi"
              />
              <v-switch
                v-model="value.tap_enabled"
                label="TAP / Cartão (SoftPOS)"
                color="primary"
                density="compact"
                hide-details
                :disabled="true"
              />
              <v-switch
                v-model="value.invoice_enabled"
                label="Cobrança no sistema (pagar depois)"
                color="primary"
                density="compact"
                hide-details
                :disabled="requestingApi"
              />
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              Pelo menos um meio deve ficar ativo. Cobrança no sistema só em vendas identificadas
              (gera fatura em Minhas faturas). TAP / Cartão (SoftPOS) está pausado por enquanto.
              Dinheiro em espécie ainda não é suportado.
            </div>
            <v-alert
              v-if="!hasPaymentRail"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              Ative Pix e/ou Cobrança no sistema para vender nesta loja
            </v-alert>
          </v-col>
          <v-col cols="12" class="text-right">
            <v-btn
              color="success"
              type="submit"
              :loading="requestingApi"
              :disabled="requestingApi || !hasPaymentRail"
            >
              <v-icon class="mr-2">mdi-content-save</v-icon>
              Salvar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>
<script>
import AutocompleteSelectCashflowCategory from './AutocompleteSelectCashflowCategory.vue'
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

const STORE_TYPES = [
  { title: 'Normal', value: 'normal' },
  { title: 'Perecíveis', value: 'pereciveis' },
  { title: 'Ingressos', value: 'ingressos' },
]
const STORE_STATUSES = [
  { title: 'Ativa', value: 'active' },
  { title: 'Inativa', value: 'inactive' },
]

export default {
  components: { AutocompleteSelectCashflowCategory },
  props: {
    user: Object,
    modelValue: Object,
  },
  emits: ['update:modelValue', 'created', 'updated'],
  data() {
    return {
      requestingApi: false,
      storeTypes: STORE_TYPES,
      storeStatuses: STORE_STATUSES,
      rules: {
        required: [(v) => !!v || 'Campo obrigatório'],
      },
      loadingManagers: false,
      managerSearch: '',
      managers: [],
      searchTimeout: null,
    }
  },
  created() {
    this.ensureModel()
  },
  methods: {
    ensureModel() {
      const vm = this
      if (!vm.modelValue?.id) {
        if (!vm.modelValue?.type) {
          vm.value = vm.generateEmpty()
        } else {
          vm.normalizePaymentFlags()
        }
      } else {
        vm.normalizeDates()
        vm.normalizePaymentFlags()
        vm.ensureManagerOption(vm.modelValue)
      }
    },
    normalizePaymentFlags() {
      const m = this.value
      if (!m) return
      // API may return 0/1; v-switch needs real booleans
      m.pix_enabled = m.pix_enabled === undefined || m.pix_enabled === null ? true : !!m.pix_enabled
      m.tap_enabled = false
      m.invoice_enabled = !!m.invoice_enabled
      m.cash_enabled = false
    },
    ensureManagerOption(store) {
      const id = store?.responsible_user_id || store?.responsible_user?.id
      if (!id) return
      if (this.managers.some((m) => m.id === id)) return
      const u = store.responsible_user || {}
      this.managers = [
        {
          id,
          name: u.name || store.responsible_user_name || `Usuário #${id}`,
          email: u.email || '',
        },
        ...this.managers,
      ]
    },
    toDateInput(val) {
      if (!val) return null
      const s = String(val)
      return s.length >= 10 ? s.substring(0, 10) : s
    },
    normalizeDates() {
      const vm = this
      if (!vm.value) return
      if (vm.value.starts_at) {
        vm.value.starts_at = vm.toDateInput(vm.value.starts_at)
      }
      if (vm.value.ends_at) {
        vm.value.ends_at = vm.toDateInput(vm.value.ends_at)
      }
    },
    generateEmpty() {
      return {
        name: '',
        type: 'pereciveis',
        status: 'active',
        starts_at: null,
        ends_at: null,
        cashflow_category_id: null,
        responsible_user_id: null,
        pix_enabled: true,
        tap_enabled: false,
        invoice_enabled: false,
        cash_enabled: false,
      }
    },
    onManagerSearch(q) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      const text = (q || '').trim()
      if (!text) {
        this.loadingManagers = false
        return
      }
      this.loadingManagers = true
      this.searchTimeout = setTimeout(() => {
        this.searchTimeout = null
        this.loadManagers(text)
      }, 400)
    },
    loadManagers(searchText) {
      const vm = this
      const groupId = api.storage.selectedGroup()?.id
      if (!groupId) {
        vm.managers = []
        vm.loadingManagers = false
        return Promise.resolve()
      }
      vm.loadingManagers = true
      return api.legalPerson
        .list(
          {
            searchText,
            withUser: true,
            itemsPerPage: 30,
            member_group_id: Number(groupId),
            active: true,
          },
          1
        )
        .then((result) => {
          const { items } = normalizeListPayload(result)
          const list = items?.length
            ? items
            : Array.isArray(result?.data)
              ? result.data
              : Array.isArray(result)
                ? result
                : []
          vm.managers = list
            .filter((lp) => lp?.user_id || lp?.user?.id)
            .map((lp) => {
              const u = lp.user || {}
              return {
                id: u.id || lp.user_id,
                name: u.name || lp.name || '',
                email: u.email || '',
              }
            })
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingManagers = false
        })
    },
    buildPayload() {
      const vm = this
      const m = vm.value || {}
      const payload = {
        name: m.name,
        type: m.type,
        status: m.status,
        starts_at: m.starts_at || null,
        ends_at: m.ends_at || null,
        cashflow_category_id: m.cashflow_category_id || null,
        responsible_user_id:
          m.responsible_user_id != null ? Number(m.responsible_user_id) : null,
        // Phase D+E: editable rails; cash stays forced off (not in product scope)
        pix_enabled: !!m.pix_enabled,
        tap_enabled: false,
        invoice_enabled: !!m.invoice_enabled,
        cash_enabled: false,
      }
      if (m.id) {
        payload.id = m.id
      }
      return payload
    },
    submitUpdate() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.value?.name) {
        vm.$notify({ type: 'warn', text: 'Nome é obrigatório' })
        return
      }
      if (!vm.value?.responsible_user_id) {
        vm.$notify({ type: 'warn', text: 'Gerente (responsável) é obrigatório' })
        return
      }
      if (!vm.hasPaymentRail) {
        vm.$notify({
          type: 'warn',
          text: 'Ative Pix e/ou Cobrança no sistema para vender nesta loja',
        })
        return
      }
      vm.requestingApi = true
      return api.store
        .updateOrCreate(vm.buildPayload())
        .then((result) => {
          if (vm.value?.id) {
            vm.$emit('updated', result)
            vm.$notify({ type: 'success', text: 'Loja atualizada com sucesso' })
          } else {
            vm.$emit('created', result)
            vm.$notify({ type: 'success', text: 'Loja criada com sucesso' })
          }
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  computed: {
    selectedGroupId() {
      return api.storage.selectedGroup()?.id || null
    },
    hasPaymentRail() {
      const m = this.value || {}
      return !!(m.pix_enabled || m.invoice_enabled)
    },
    managerItems() {
      return (this.managers || []).map((u) => ({
        title: u.email ? `${u.name} (${u.email})` : u.name || `Usuário #${u.id}`,
        value: u.id,
      }))
    },
    managerNoDataText() {
      if (this.loadingManagers) return 'Buscando…'
      if (!(this.managerSearch || '').trim()) return 'Digite para buscar membros do grupo'
      return 'Nenhum usuário encontrado'
    },
    value: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal?.id) {
        const needsNorm =
          (newVal.starts_at && String(newVal.starts_at).length > 10) ||
          (newVal.ends_at && String(newVal.ends_at).length > 10)
        if (needsNorm) this.normalizeDates()
        this.normalizePaymentFlags()
        this.ensureManagerOption(newVal)
      } else if (!newVal?.type) {
        // re-seed defaults after dialog clears model
        this.value = this.generateEmpty()
      } else {
        this.normalizePaymentFlags()
      }
    },
    managerSearch(newVal) {
      this.onManagerSearch(newVal)
    },
  },
}
</script>
